import * as React from 'react'
import { Link, useFetcher, useNavigate, useSearchParams } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '#app/components/ui/dialog.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'

type WizardRole = 'lawyer' | 'student' | 'legal-assistant' | 'other'
type LegalWorkType =
	| 'notarial'
	| 'corporate'
	| 'civil-litigation'
	| 'criminal-litigation'
	| 'family'
	| 'labor'
	| 'administrative'
	| 'tax'
	| 'real-estate'
	| 'research-study'
	| 'other'
type PrimaryGoal = 'auri' | 'legal-search' | 'documents' | 'drafting' | 'exams' | 'organization'
type WizardStep = 'role' | 'work' | 'goals' | 'thanks' | 'features' | 'more' | 'start'
type StepDirection = 'forward' | 'back'

type WelcomeOnboardingResult = { status: 'success'; ctaHref: string } | { status: 'error'; message: string } | undefined

const welcomeBannerSrc = '/img/dialog-banners/atmosphere-soft-glow.webp'

const roles: Array<{ value: WizardRole; label: string; description: string; icon: IconName }> = [
	{ value: 'lawyer', label: 'Abogado/a', description: 'Ejerzo o asesoro profesionalmente.', icon: 'scale' },
	{
		value: 'student',
		label: 'Estudiante',
		description: 'Estoy estudiando Derecho, preparándome para el éxamen',
		icon: 'exam',
	},
	{
		value: 'legal-assistant',
		label: 'Asistente legal',
		description: 'Apoyo labores legales o notariales.',
		icon: 'briefcase',
	},
	{ value: 'other', label: 'Otro', description: 'Uso IUS PRO para trabajo relacionado.', icon: 'user-filled' },
]

const legalWorkTypes: Array<{ value: LegalWorkType; label: string }> = [
	{ value: 'notarial', label: 'Notarial' },
	{ value: 'corporate', label: 'Corporativo' },
	{ value: 'civil-litigation', label: 'Litigio civil' },
	{ value: 'criminal-litigation', label: 'Litigio penal' },
	{ value: 'family', label: 'Familia' },
	{ value: 'labor', label: 'Laboral' },
	{ value: 'administrative', label: 'Administrativo' },
	{ value: 'tax', label: 'Tributario' },
	{ value: 'real-estate', label: 'Inmobiliario' },
	{ value: 'research-study', label: 'Investigación / estudio' },
	{ value: 'other', label: 'Otro' },
]

const goals: Array<{ value: PrimaryGoal; label: string; description: string; icon: IconName }> = [
	{
		value: 'auri',
		label: 'Consultar con Auri',
		description: 'Resolver dudas, investigación jurídica y analizar documentos.',
		icon: 'ai',
	},
	{
		value: 'legal-search',
		label: 'Investigar fuentes',
		description: 'Encontrar normativa y jurisprudencia.',
		icon: 'search-filled',
	},
	{
		value: 'documents',
		label: 'Analizar expedientes',
		description: 'Análisis profundo de un expediente completo',
		icon: 'folder',
	},
	{
		value: 'drafting',
		label: 'Redactar documentos',
		description: 'Crear y mejorar borradores legales.',
		icon: 'write',
	},
	{
		value: 'exams',
		label: 'Practicar exámenes',
		description: 'Estudiar con preguntas y retroalimentación.',
		icon: 'exam',
	},
	{
		value: 'organization',
		label: 'Organizar trabajo',
		description: 'Gestionar clientes, tablas y alertas.',
		icon: 'layer-filled',
	},
]

const mainFeatures = {
	student: [
		{
			title: 'Exámenes',
			body: 'Usa Exámenes cuando quieras practicar una materia, medir tu avance o prepararte con retroalimentación.',
			icon: 'exam' as const,
			tone: 'bg-[#f2eaf8] text-[#5f4b7a]',
		},
		{
			title: 'Auri Chat',
			body: 'Auri es la base de nuetra plataforma, sirve para investigación jurídica, análisis de documentos/contratos/etc..., resolver dudas y mucho más.',
			icon: 'ai' as const,
			tone: 'bg-[#e8edf7] text-[#445c7a]',
		},
		{
			title: 'Buscador legal',
			body: 'Usa el Buscador cuando necesites encontrar normativa, jurisprudencia o fuentes para una tarea, mucho más rápido y preciso que buscar en Nexus o Sinalevi',
			icon: 'search-filled' as const,
			tone: 'bg-[#edf3ee] text-[#49634d]',
		},
		{
			title: 'Transcripciones',
			body: 'Usa Transcripciones cuando quieras convertir una clase, entrevista o audio en un documento de texto',
			icon: 'transcription' as const,
			tone: 'bg-[#f6ece7] text-[#7a5245]',
		},
	],
	default: [
		{
			title: 'Auri Chat',
			body: 'Auri es la base de nuetra plataforma, sirve para investigación jurídica, análisis de documentos/contratos/etc..., resolver dudas y mucho más.',
			icon: 'ai' as const,
			tone: 'bg-[#e8edf7] text-[#445c7a]',
		},
		{
			title: 'Buscador legal',
			body: 'Usa el Buscador cuando necesites encontrar normativa, jurisprudencia o fuentes para una tarea, mucho más rápido y preciso que buscar en Nexus o Sinalevi',
			icon: 'search-filled' as const,
			tone: 'bg-[#edf3ee] text-[#49634d]',
		},
		{
			title: 'Expedientes',
			body: 'Usa Expedientes cuando tengas varios documentos de un caso y quieras ordenar hechos, resúmenes y puntos clave.',
			icon: 'folder' as const,
			tone: 'bg-[#f2eaf8] text-[#5f4b7a]',
		},
		{
			title: 'Redactor',
			body: 'Usa Redactor cuando necesites crear, corregir o mejorar un borrador legal, el redactor siempre usa la normativa actualizada a la fecha.',
			icon: 'write' as const,
			tone: 'bg-[#f6ece7] text-[#7a5245]',
		},
	],
}

const moreTools = [
	{
		title: 'Bases de conocimiento',
		body: 'Haz que Auri use tus políticas y fuentes internas en sus respuestas.',
		icon: 'database' as const,
		href: '/guias/bases-de-conocimiento-como-usar',
	},
	{
		title: 'Clientes',
		body: 'Organiza la información de personas, empresas y asuntos frecuentes.',
		icon: 'layer-filled' as const,
		href: '/guias/proyectos-como-usar',
	},
	{
		title: 'Tablas IA',
		body: 'Extrae y ordena datos de muchos documentos en una sola tabla.',
		icon: 'filled-table' as const,
		href: '/guias/tablas-ia-como-usar',
	},
	{
		title: 'Notificaciones',
		body: 'Recibe alertas de La Gaceta y el Boletín Judicial sobre los temas que te interesan.',
		icon: 'bell' as const,
		href: '/guias/notificaciones-como-usar',
	},
]

export function WelcomeWizardDialog({ isOrganizationMember }: { isOrganizationMember: boolean }) {
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const fetcher = useFetcher<WelcomeOnboardingResult>()
	const [step, setStep] = React.useState(0)
	const [stepDirection, setStepDirection] = React.useState<StepDirection>('forward')
	const [role, setRole] = React.useState<WizardRole | null>(null)
	const [selectedWorkTypes, setSelectedWorkTypes] = React.useState<LegalWorkType[]>([])
	const [primaryGoals, setPrimaryGoals] = React.useState<PrimaryGoal[]>([])
	const isOpen = searchParams.get('welcome') === '1'
	const isStudent = role === 'student'
	const steps: WizardStep[] = isOrganizationMember
		? ['goals', 'thanks', 'features', 'more', 'start']
		: isStudent
			? ['role', 'thanks', 'features', 'more', 'start']
			: ['role', 'work', 'goals', 'thanks', 'features', 'more', 'start']
	const current = steps[step] ?? 'start'
	const submitting = fetcher.state !== 'idle'
	const canContinue =
		current === 'role'
			? Boolean(role)
			: current === 'work'
				? selectedWorkTypes.length > 0
				: current === 'goals'
					? primaryGoals.length > 0
					: true

	React.useEffect(() => {
		if (fetcher.data?.status !== 'success') return
		void navigate(fetcher.data.ctaHref, { replace: true })
	}, [fetcher.data, navigate])

	function closeDialog() {
		const nextParams = new URLSearchParams(searchParams)
		nextParams.delete('welcome')
		const nextSearch = nextParams.toString()
		void navigate(`${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true })
	}

	function toggleWorkType(value: LegalWorkType) {
		setSelectedWorkTypes(current =>
			current.includes(value) ? current.filter(item => item !== value) : [...current, value],
		)
	}

	function toggleGoal(value: PrimaryGoal) {
		setPrimaryGoals(current => (current.includes(value) ? current.filter(item => item !== value) : [...current, value]))
	}

	function submit() {
		const formData = new FormData()
		if (!isOrganizationMember && role) formData.set('role', role)
		formData.set('legalWorkTypes', JSON.stringify(isOrganizationMember || isStudent ? [] : selectedWorkTypes))
		formData.set('primaryGoals', JSON.stringify(isStudent ? [] : primaryGoals))
		void fetcher.submit(formData, { method: 'POST', action: '/resources/welcome-onboarding' })
	}

	function goNext() {
		if (step === steps.length - 1) return submit()
		setStepDirection('forward')
		setStep(current => Math.min(current + 1, steps.length - 1))
	}

	function goBack() {
		setStepDirection('back')
		setStep(current => Math.max(current - 1, 0))
	}

	const featureSet = isStudent ? mainFeatures.student : mainFeatures.default
	const isCenteredStep = current === 'thanks' || current === 'start'
	const isCompletionStep = current === 'start'
	const isContentHeavyStep = current === 'features' || current === 'more'
	const primaryButtonLabel = step === steps.length - 1 ? (isStudent ? 'Ir a Exámenes' : 'Abrir Auri Chat') : 'Continuar'
	const currentEyebrow = getEyebrow(current)

	if (!isOpen) return null

	return (
		<Dialog open onOpenChange={open => (!open ? closeDialog() : undefined)}>
			<DialogContent
				onOpenAutoFocus={event => event.preventDefault()}
				overlayClassName="bg-[#172033]/25 backdrop-blur-[6px] [@media(prefers-reduced-transparency:reduce)]:bg-[#172033]/40 [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none"
				className={cn(
					'w-[min(94vw,720px)] max-w-[720px] gap-0 overflow-hidden rounded-[24px] border-[#dfe4ec] bg-[#fbfcff] p-0 text-[#171927] shadow-[0_32px_100px_rgba(27,38,68,0.24),0_2px_10px_rgba(27,38,68,0.08)] contrast-more:border-[#6d7892] sm:rounded-[30px] [&>button]:right-4 [&>button]:top-4 [&>button]:z-30 [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center [&>button]:bg-white/55 [&>button]:p-0 [&>button]:text-[#263554] [&>button]:!opacity-100 [&>button]:shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_4px_16px_rgba(48,64,108,0.12)] [&>button]:backdrop-blur-xl [&>button]:transition-[background-color,transform] [&>button]:duration-150 [&>button]:hover:bg-white/75 [&>button]:focus:ring-[#4f73d1] [&>button]:active:scale-[0.94] [@media(prefers-reduced-transparency:reduce)]:[&>button]:bg-white [@media(prefers-reduced-transparency:reduce)]:[&>button]:backdrop-blur-none',
					isContentHeavyStep ? 'sm:max-h-[98dvh]' : null,
				)}
			>
				<div
					className={cn(
						'flex h-[min(90dvh,760px)] min-h-0 flex-col bg-[#fbfcff] sm:h-[min(92dvh,840px)]',
						isContentHeavyStep ? 'sm:h-[min(98dvh,900px)]' : null,
					)}
				>
					<header
						className={cn(
							'relative h-[132px] shrink-0 overflow-hidden sm:h-[168px]',
							isContentHeavyStep ? 'sm:h-[132px]' : null,
						)}
					>
						<img
							src={welcomeBannerSrc}
							alt=""
							aria-hidden="true"
							draggable={false}
							className="absolute inset-0 h-full w-full object-cover"
						/>
						<div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-[#fbfcff]/15" />
						<div className="absolute left-4 top-4 flex h-10 items-center gap-2 rounded-full bg-white/50 px-3.5 text-[#22345d] shadow-[inset_0_1px_0_rgba(255,255,255,0.75),0_4px_18px_rgba(56,74,124,0.10)] backdrop-blur-xl sm:left-5 sm:top-5 [@media(prefers-reduced-transparency:reduce)]:bg-white [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none">
							<Icon name="sparkles" className="h-4 w-4" />
							<span className="text-sm font-semibold tracking-[-0.01em]">IUS PRO</span>
						</div>
						<div
							className="absolute inset-x-0 bottom-5 flex items-center justify-center gap-3 sm:bottom-6"
							aria-hidden="true"
						>
							<div className="flex h-12 w-12 items-center justify-center rounded-[15px] border border-white/65 bg-white/30 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_24px_rgba(61,81,139,0.14)] backdrop-blur-xl sm:h-14 sm:w-14 sm:rounded-[17px]">
								<Icon
									name="user-filled"
									className="h-5 w-5 drop-shadow-[0_1px_3px_rgba(51,70,126,0.28)] sm:h-6 sm:w-6"
								/>
							</div>
							<div className="flex items-center gap-1.5">
								<span className="h-1.5 w-1.5 rounded-full bg-white/80" />
								<span className="h-1.5 w-1.5 rounded-full bg-white/65" />
								<span className="h-1.5 w-1.5 rounded-full bg-white/50" />
							</div>
							<div className="flex h-12 w-12 items-center justify-center rounded-[15px] border border-white/65 bg-white/30 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.72),0_8px_24px_rgba(61,81,139,0.14)] backdrop-blur-xl sm:h-14 sm:w-14 sm:rounded-[17px]">
								<Icon name="sparkles" className="h-5 w-5 drop-shadow-[0_1px_3px_rgba(51,70,126,0.28)] sm:h-6 sm:w-6" />
							</div>
						</div>
					</header>

					<div className="shrink-0 px-5 pt-4 sm:px-8 sm:pt-5">
						<div className="mb-2 flex items-center justify-between gap-4 text-xs font-semibold tracking-[0.04em] text-[#66708a]">
							<span>Bienvenido</span>
							<span className="tabular-nums">
								Paso {step + 1} de {steps.length}
							</span>
						</div>
						<div
							className="grid grid-cols-[repeat(var(--step-count),minmax(0,1fr))] gap-1.5"
							style={{ '--step-count': steps.length } as React.CSSProperties}
							role="progressbar"
							aria-label="Progreso de bienvenida"
							aria-valuemin={1}
							aria-valuemax={steps.length}
							aria-valuenow={step + 1}
						>
							{steps.map((item, index) => (
								<div
									key={item}
									className={cn(
										'h-1.5 rounded-full transition-colors duration-300 ease-soft motion-reduce:duration-0',
										index <= step ? 'bg-[#4f73d1]' : 'bg-[#e6e9f1]',
									)}
								/>
							))}
						</div>
					</div>

					<div
						className={cn(
							'custom-scrollbar flex-1 overflow-y-auto px-5 py-5 sm:px-8 sm:py-6',
							isContentHeavyStep ? 'sm:py-4' : null,
						)}
						data-step-direction={stepDirection}
					>
						<div key={current} className="welcome-wizard-step">
							<DialogHeader
								className={cn(
									'mx-auto mb-6 max-w-[590px] space-y-2 text-center sm:mb-7',
									isCenteredStep ? 'max-w-[520px] pt-5 sm:pt-7' : null,
									isCompletionStep ? 'sm:text-center' : null,
									isContentHeavyStep ? 'sm:mb-5' : null,
								)}
							>
								{currentEyebrow ? (
									<p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#4f73d1]">{currentEyebrow}</p>
								) : null}
								<DialogTitle
									className={cn(
										'mb-0 font-sans text-[1.8rem] font-semibold leading-[1.1] tracking-[-0.035em] text-[#191b29] sm:text-[2.1rem]',
										isCenteredStep ? 'sm:text-[2.3rem]' : null,
									)}
								>
									{getTitle(current, isOrganizationMember)}
								</DialogTitle>
								<DialogDescription
									className={cn(
										'mx-auto max-w-[560px] text-[0.9375rem] leading-6 text-[#66708a] sm:text-base sm:leading-7',
										isCenteredStep ? 'max-w-[500px]' : null,
									)}
								>
									{getDescription(current, isOrganizationMember, isStudent)}
								</DialogDescription>
							</DialogHeader>

							<div className={cn(isCenteredStep ? 'mx-auto max-w-[580px]' : null)}>{renderStep()}</div>
						</div>
					</div>

					<footer className="bg-[#fbfcff]/92 relative flex shrink-0 items-center justify-between gap-3 px-5 py-4 shadow-[0_-16px_36px_-30px_rgba(27,38,68,0.7)] backdrop-blur-xl sm:px-8 [@media(prefers-reduced-transparency:reduce)]:bg-[#fbfcff] [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none">
						<Button
							type="button"
							variant="ghost"
							onClick={() => (step === 0 ? closeDialog() : goBack())}
							className="h-11 rounded-xl px-2 text-sm font-semibold text-[#30364a] hover:bg-[#edf0f7] hover:text-[#3455ae] focus-visible:ring-2 focus-visible:ring-[#4f73d1] focus-visible:ring-offset-2 sm:text-base"
						>
							{step === 0 ? null : <Icon name="arrow-left" className="mr-2 h-4 w-4" />}
							{step === 0 ? 'Cerrar' : 'Atrás'}
						</Button>
						<div className="flex min-w-0 items-center gap-3">
							{fetcher.data?.status === 'error' ? (
								<p className="max-w-[240px] text-sm text-destructive">{fetcher.data.message}</p>
							) : null}
							<Button
								type="button"
								onClick={goNext}
								disabled={!canContinue || submitting}
								className="h-11 rounded-[14px] bg-[#3f63c6] px-4 text-sm font-semibold text-[#f8f9ff] shadow-[inset_0_1px_0_rgba(255,255,255,0.20),0_7px_20px_rgba(63,99,198,0.22)] transition-[background-color,box-shadow,transform] duration-150 hover:bg-[#3455ae] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.20),0_9px_24px_rgba(63,99,198,0.28)] focus-visible:ring-2 focus-visible:ring-[#4f73d1] focus-visible:ring-offset-2 disabled:bg-[#b7c2d9] disabled:text-[#f8f9ff] disabled:shadow-none sm:px-5 sm:text-base"
							>
								{submitting ? <Icon name="spinner" className="mr-2 h-4 w-4 animate-spin" /> : null}
								{primaryButtonLabel}
								{!submitting ? <Icon name="arrow-right" className="ml-2 h-4 w-4" /> : null}
							</Button>
						</div>
					</footer>
				</div>
			</DialogContent>
		</Dialog>
	)

	function renderStep() {
		switch (current) {
			case 'role':
				return (
					<div className="divide-y divide-[#e5e8ef] overflow-hidden rounded-[22px] border border-[#dfe4ec] bg-[#fefeff] shadow-[0_1px_2px_rgba(35,47,80,0.04)]">
						{roles.map(option => (
							<SelectCard
								key={option.value}
								selected={role === option.value}
								title={option.label}
								body={option.description}
								icon={option.icon}
								mode="single"
								onClick={() => setRole(option.value)}
							/>
						))}
					</div>
				)
			case 'work':
				return (
					<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
						{legalWorkTypes.map(option => (
							<WorkAreaOption
								key={option.value}
								selected={selectedWorkTypes.includes(option.value)}
								label={option.label}
								onClick={() => toggleWorkType(option.value)}
							/>
						))}
					</div>
				)
			case 'goals':
				return (
					<div className="divide-y divide-[#e5e8ef] overflow-hidden rounded-[22px] border border-[#dfe4ec] bg-[#fefeff] shadow-[0_1px_2px_rgba(35,47,80,0.04)]">
						{goals
							.filter(goal => isStudent || goal.value !== 'exams')
							.map(goal => (
								<SelectCard
									key={goal.value}
									selected={primaryGoals.includes(goal.value)}
									title={goal.label}
									body={goal.description}
									icon={goal.icon}
									mode="multiple"
									onClick={() => toggleGoal(goal.value)}
								/>
							))}
					</div>
				)
			case 'thanks':
				return (
					<div className="rounded-[22px] border border-[#d9e2f5] bg-[linear-gradient(135deg,#f8faff_0%,#eef3ff_100%)] p-5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_12px_30px_rgba(63,99,198,0.08)] sm:p-6">
						<div className="flex items-start gap-4 sm:gap-5">
							<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[16px] border border-white bg-white/85 text-[#3f63c6] shadow-[0_6px_18px_rgba(63,99,198,0.12)] sm:h-14 sm:w-14">
								<Icon name="calendar" className="h-5 w-5 sm:h-6 sm:w-6" />
							</div>
							<div className="min-w-0 flex-1">
								<h3 className="text-base font-semibold leading-snug tracking-[-0.015em] text-[#1c2030] sm:text-[1.0625rem]">
									¿Quieres aprovechar IUS PRO al máximo?
								</h3>
								<p className="mt-1.5 text-sm leading-6 text-[#68728a]">
									Agenda una demostración personalizada con nuestro equipo y descubre cómo adaptar IUS PRO a tu práctica
									o estudios.
								</p>
								<p className="mt-2 text-xs font-medium text-[#7a8499]">Es opcional; puedes continuar cuando quieras.</p>
							</div>
						</div>
						<Link
							to="/links/demo"
							target="_blank"
							rel="noreferrer"
							className="mt-5 inline-flex h-11 w-full items-center justify-center rounded-[14px] border border-[#c9d5f2] bg-white px-4 text-sm font-semibold text-[#3455ae] shadow-[0_4px_12px_rgba(63,99,198,0.08)] outline-none transition-[background-color,border-color,box-shadow,transform] duration-150 hover:border-[#aebfe8] hover:bg-[#fbfcff] hover:shadow-[0_6px_16px_rgba(63,99,198,0.12)] focus-visible:ring-2 focus-visible:ring-[#4f73d1] focus-visible:ring-offset-2 active:scale-[0.985]"
						>
							Agendar demostración
							<Icon name="external-link" className="ml-2 h-3.5 w-3.5" />
						</Link>
					</div>
				)
			case 'features':
				return (
					<div className="divide-y divide-[#e5e8ef] overflow-hidden rounded-[22px] border border-[#dfe4ec] bg-[#fefeff] shadow-[0_1px_2px_rgba(35,47,80,0.04)]">
						{featureSet.map(feature => (
							<FeatureSummary key={feature.title} {...feature} />
						))}
					</div>
				)
			case 'more':
				return (
					<div className="divide-y divide-[#e5e8ef] overflow-hidden rounded-[22px] border border-[#dfe4ec] bg-[#fefeff] shadow-[0_1px_2px_rgba(35,47,80,0.04)]">
						{moreTools.map(tool => (
							<GuideSummary key={tool.title} {...tool} />
						))}
					</div>
				)
			case 'start':
				return (
					<div className="mx-auto flex max-w-[460px] flex-col items-center text-center">
						<div className="relative flex h-20 w-20 items-center justify-center rounded-[26px] border border-[#cfdaf5] bg-[linear-gradient(145deg,#f8faff_0%,#e7eeff_100%)] text-[#3f63c6] shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_14px_34px_rgba(63,99,198,0.16)] sm:h-24 sm:w-24 sm:rounded-[30px]">
							<span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full border border-white bg-white text-[#6f8bdd] shadow-[0_5px_14px_rgba(63,99,198,0.14)]">
								<Icon name="sparkles" className="h-3.5 w-3.5" />
							</span>
							<Icon name="check" className="h-8 w-8 sm:h-10 sm:w-10" />
						</div>
						<h3 className="mt-5 text-lg font-semibold tracking-[-0.02em] text-[#1c2030] sm:text-xl">
							¡Bienvenido a IUS PRO!
						</h3>
						<p className="mt-2 max-w-[420px] text-sm leading-6 text-[#68728a] sm:text-[0.9375rem]">
							Tus herramientas están preparadas para acompañarte desde tu primera consulta.
						</p>
					</div>
				)
			default:
				return null
		}
	}
}

function WorkAreaOption({ selected, label, onClick }: { selected: boolean; label: string; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={selected}
			className={cn(
				'group flex min-h-14 w-full items-center gap-3 rounded-[16px] border px-3.5 py-2.5 text-left text-sm font-semibold leading-5 tracking-[-0.01em] outline-none transition-[background-color,border-color,color,box-shadow,transform] duration-150 focus-visible:ring-2 focus-visible:ring-[#4f73d1] focus-visible:ring-offset-2 active:scale-[0.98] sm:min-h-[60px] sm:px-4 sm:text-[0.9375rem]',
				selected
					? 'border-[#6f8bdd] bg-[#eef3ff] text-[#294c9d] shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_5px_14px_rgba(63,99,198,0.10)]'
					: 'border-[#d9dee9] bg-[#fefeff] text-[#30364a] shadow-[0_1px_2px_rgba(35,47,80,0.03)] hover:border-[#aab7d8] hover:bg-[#f8faff]',
			)}
		>
			<span className="min-w-0 flex-1">{label}</span>
			<span
				className={cn(
					'flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border transition-[background-color,border-color,color] duration-150',
					selected
						? 'border-[#3f63c6] bg-[#3f63c6] text-white'
						: 'border-[#cbd3e2] bg-white text-transparent group-hover:border-[#aab7d8]',
				)}
			>
				<Icon name="check" className="h-3 w-3" />
			</span>
		</button>
	)
}

function SelectCard({
	selected,
	title,
	body,
	icon,
	mode,
	onClick,
}: {
	selected: boolean
	title: string
	body: string
	icon: IconName
	mode: 'single' | 'multiple'
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={selected}
			className={cn(
				'group flex min-h-[76px] w-full items-center gap-3.5 bg-transparent px-4 py-3.5 text-left outline-none transition-[background-color,box-shadow,transform] duration-150 focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4f73d1] active:scale-[0.995] sm:min-h-[82px] sm:px-5 sm:py-4',
				selected ? 'bg-[#eef3ff] shadow-[inset_3px_0_0_#4f73d1]' : 'hover:bg-[#f7f8fc]',
			)}
		>
			<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#eef1f9] text-[#4a5f96] transition-colors group-hover:bg-[#e8ecf6] sm:h-11 sm:w-11">
				<Icon name={icon} className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
			</span>
			<span className="min-w-0 flex-1">
				<span className="block text-base font-semibold leading-tight tracking-[-0.01em] text-[#1c2030] sm:text-[1.0625rem]">
					{title}
				</span>
				<span className="mt-1 block text-sm leading-5 text-[#68728a] sm:leading-6">{body}</span>
			</span>
			<span
				className={cn(
					'flex h-5 w-5 shrink-0 items-center justify-center border transition-colors sm:h-6 sm:w-6',
					mode === 'multiple' ? 'rounded-lg' : 'rounded-full',
					selected ? 'border-[#3f63c6] bg-[#3f63c6] text-white' : 'border-[#cfd5e2] bg-white text-transparent',
				)}
			>
				<Icon name="check" className="h-3 w-3" />
			</span>
		</button>
	)
}

function FeatureSummary({ title, body, icon, tone }: { title: string; body: string; icon: IconName; tone: string }) {
	return (
		<div className="flex items-start gap-3.5 p-4 sm:gap-4">
			<div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] sm:h-11 sm:w-11', tone)}>
				<Icon name={icon} className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
			</div>
			<div className="min-w-0 flex-1">
				<h3 className="text-base font-semibold leading-tight tracking-[-0.01em] text-[#1c2030] sm:text-[1.0625rem]">
					{title}
				</h3>
				<p className="mt-1.5 text-sm leading-6 text-[#68728a] sm:text-[0.9375rem] sm:leading-6">{body}</p>
			</div>
		</div>
	)
}

function GuideSummary({ title, body, icon, href }: { title: string; body: string; icon: IconName; href: string }) {
	return (
		<div className="flex items-start gap-3.5 p-4 sm:gap-4">
			<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-[#eef1f9] text-[#4a5f96] sm:h-11 sm:w-11">
				<Icon name={icon} className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
			</div>
			<div className="min-w-0 flex-1">
				<h3 className="text-base font-semibold leading-tight tracking-[-0.01em] text-[#1c2030] sm:text-[1.0625rem]">
					{title}
				</h3>
				<p className="mt-1.5 text-sm leading-6 text-[#68728a] sm:text-[0.9375rem] sm:leading-6">{body}</p>
			</div>
			<Link
				to={href}
				target="_blank"
				rel="noreferrer"
				className="inline-flex h-11 shrink-0 items-center rounded-full bg-[#eef3ff] px-3.5 text-xs font-semibold text-[#3455ae] outline-none transition-[background-color,transform] duration-150 hover:bg-[#e3eaff] focus-visible:ring-2 focus-visible:ring-[#4f73d1] focus-visible:ring-offset-2 active:scale-[0.96]"
			>
				Ver guía
			</Link>
		</div>
	)
}

function getEyebrow(step: WizardStep) {
	switch (step) {
		case 'features':
			return 'Funciones principales'
		case 'more':
			return 'Más herramientas y guías'
		case 'thanks':
			return 'Perfil listo'
		case 'start':
			return 'Tu espacio está listo'
		default:
			return 'Personaliza tu experiencia'
	}
}

function getTitle(step: WizardStep, isOrganizationMember: boolean) {
	switch (step) {
		case 'role':
			return '¿Qué te describe mejor?'
		case 'work':
			return '¿En qué áreas trabajas o estudias?'
		case 'goals':
			return isOrganizationMember ? '¿Qué quieres hacer primero?' : '¿Qué quieres hacer con IUS PRO?'
		case 'thanks':
			return 'Tu perfil está listo.'
		case 'features':
			return 'Tus herramientas clave'
		case 'more':
			return 'Herramientas para flujos específicos'
		case 'start':
			return '¡Todo listo para empezar!'
	}
}

function getDescription(step: WizardStep, isOrganizationMember: boolean, isStudent: boolean) {
	switch (step) {
		case 'role':
			return 'Esto nos ayuda a personalizar tu experiencia en IUS PRO.'
		case 'work':
			return 'Puedes elegir más de una opción.'
		case 'goals':
			return isOrganizationMember
				? 'Tu organización ya define el acceso. Elige una o varias formas de empezar.'
				: 'Elige una o varias opciones. No limita lo que puedes usar después.'
		case 'thanks':
			return 'A continuación verás un recorrido breve por las herramientas principales.'
		case 'features':
			return 'Esto es lo que puedes hacer desde el primer día.'
		case 'more':
			return 'También puedes apoyarte en estas guías cuando necesites organizar fuentes, clientes, datos o alertas.'
		case 'start':
			return isStudent
				? 'Te llevaremos a Exámenes para que puedas practicar desde el inicio.'
				: 'Te llevaremos a Auri Chat para que hagas tu primera consulta.'
	}
}
